import { createAsyncThunk } from '@reduxjs/toolkit';
import { loginRequest, logoutRequest } from './authApi';
import { logoutLocal } from './authSlice';
import { persistor } from '../../store/store';
import { extractApiError } from '../../utils/errors';
import { clearPersistedKeys } from './clearPersistedKeys';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password, navigate }, { rejectWithValue }) => {
    try {
      const { data, status } = await loginRequest({ email, password });

      if (status !== 200) {
        throw new Error('Error de inicio de sesión');
      }

      // Everybody the same way, and the router decides who does not belong
      // here. This used to branch on the profile and send two of them to
      // `/driver/my-routes` and `/admin_checkout/delivery-resume` — real
      // addresses, but on the *other* front. Here they matched nothing, so an
      // account on the checkout plan landed on the admin workspace's own 404
      // with the whole TMS sidebar around it. The decision belongs in the
      // router anyway: it is the only place that also sees a session restored
      // from disk, which is most of them.
      navigate('/dashboard');

      return data; // This becomes action.payload
    } catch (error) {
      console.error(error);
      // This used to store `response.data` whole, which made the login screen
      // the worst place in the app to be when the backend is down: an HTML error
      // page got painted inside the form, and a backend that did not answer at
      // all fell through to "Credenciales Inválidas" — blaming the user's
      // password for a missing server.
      return rejectWithValue(extractApiError(error, 'Credenciales Inválidas'));
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (navigate, { dispatch }) => {
    // 1. Remove locally immediately
    dispatch(logoutLocal());
    if (navigate) navigate('/login');

    // 2. Call backend
    try {
      await logoutRequest();
    } catch (error) {
      console.error('Fallo al desloguear en el backend:', error);
    }

    // 3. Rebuild the app from scratch.
    //
    // `logoutLocal` only resets the auth slice, and a client-side navigation
    // never rebuilds the store, so every other slice keeps its data. The
    // cached catalogs are the worst case: they also keep their `...Loaded`
    // flag, and the `condition` on their thunks reads it, so the next account
    // signing in on this machine would never refetch them.
    //
    // `purge` clears the persisted keys but not the state in memory, which is
    // why the reload is what actually does the resetting. It runs in `finally`
    // so a failed purge can never leave the session half closed, and mirrors
    // what the 401 interceptor already does when a session expires.
    // `purge` alone left the Activity Center of the previous account behind
    // about two times out of three: it does await every nested key, but the
    // persistoid drains its queue on a `setInterval`, so the writes `logoutLocal`
    // queued land *after* the removal and put the keys back. Pause, drain, then
    // purge — and sweep whatever is left synchronously, so this does not rest on
    // the library's timing.
    try {
      persistor.pause();
      await persistor.flush();
      await persistor.purge();
    } catch (error) {
      console.error('Fallo al limpiar el almacenamiento persistido:', error);
    } finally {
      clearPersistedKeys();
      window.location.replace('/login');
    }
  }
);
